import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { 
  Mail, 
  CheckCircle, 
  XCircle,
  ExternalLink,
  LogOut,
  User,
  Star,
  Plus,
  Trash2,
  Pencil,
  Send,
  Loader2
} from "lucide-react";

export const SettingsPage = () => {
  const [searchParams] = useSearchParams();
  const [gmailStatus, setGmailStatus] = useState({ connected: false, accounts: [] });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [accountToDisconnect, setAccountToDisconnect] = useState(null);
  const [editDisplayNameOpen, setEditDisplayNameOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [displayName, setDisplayName] = useState("");

  useEffect(() => {
    fetchGmailStatus();
    
    // Check for OAuth callback status
    const gmailParam = searchParams.get("gmail");
    const errorParam = searchParams.get("error");
    
    if (gmailParam === "connected") {
      toast.success("Gmail account connected successfully!");
      window.history.replaceState({}, "", "/settings");
    } else if (errorParam) {
      toast.error("Failed to connect Gmail account. Please try again.");
      window.history.replaceState({}, "", "/settings");
    }
    
    // Listen for messages from OAuth popup
    const handleMessage = (event) => {
      if (event.data?.type === 'oauth-complete') {
        setConnecting(false);
        if (event.data.status === 'success') {
          toast.success(`Gmail account ${event.data.email || ''} connected successfully!`);
          fetchGmailStatus();
        } else {
          toast.error("Failed to connect Gmail account.");
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [searchParams]);

  const fetchGmailStatus = async () => {
    try {
      const response = await apiClient.get("/oauth/gmail/status");
      setGmailStatus(response.data);
    } catch (error) {
      console.error("Error fetching Gmail status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await apiClient.get("/oauth/gmail/login");
      // Open Google OAuth in a new popup window to avoid blocking
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const popup = window.open(
        response.data.auth_url,
        'gmail-oauth',
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
      
      // Poll for popup close or success
      const pollTimer = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(pollTimer);
          setConnecting(false);
          // Refresh status after popup closes
          setTimeout(() => {
            fetchGmailStatus();
          }, 1000);
        }
      }, 500);
      
    } catch (error) {
      toast.error("Failed to initiate Gmail connection");
      setConnecting(false);
    }
  };

  const handleSetPrimary = async (accountId) => {
    try {
      await apiClient.post(`/oauth/gmail/accounts/${accountId}/set-primary`);
      toast.success("Primary account updated");
      fetchGmailStatus();
    } catch (error) {
      toast.error("Failed to set primary account");
    }
  };

  const handleDisconnect = async () => {
    if (!accountToDisconnect) return;
    try {
      await apiClient.delete(`/oauth/gmail/accounts/${accountToDisconnect.id}`);
      toast.success(`${accountToDisconnect.email} disconnected`);
      fetchGmailStatus();
    } catch (error) {
      toast.error("Failed to disconnect account");
    }
    setDisconnectDialogOpen(false);
    setAccountToDisconnect(null);
  };

  const openDisconnectDialog = (account) => {
    setAccountToDisconnect(account);
    setDisconnectDialogOpen(true);
  };

  const openEditDisplayName = (account) => {
    setEditingAccount(account);
    setDisplayName(account.display_name || account.name || "");
    setEditDisplayNameOpen(true);
  };

  const handleUpdateDisplayName = async () => {
    if (!editingAccount) return;
    try {
      await apiClient.put(`/oauth/gmail/accounts/${editingAccount.id}/display-name`, {
        display_name: displayName.trim()
      });
      toast.success("Display name updated");
      fetchGmailStatus();
    } catch (error) {
      toast.error("Failed to update display name");
    }
    setEditDisplayNameOpen(false);
    setEditingAccount(null);
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await apiClient.post("/oauth/gmail/test-connection");
      if (response.data.success) {
        toast.success(`Test email sent! Check your inbox at ${gmailStatus.email || 'your Gmail'}`);
      } else {
        toast.error(response.data.error || "Test failed");
        // Show more details in console for debugging
        console.error("Gmail test error:", response.data.raw_error);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.response?.data?.error || "Failed to test connection";
      toast.error(errorMessage);
      console.error("Gmail test error:", error.response?.data);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your account and integrations</p>
      </div>

      {/* Gmail Connection */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Email Accounts
              </CardTitle>
              <CardDescription>
                Connect Gmail accounts to send emails through your campaigns
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {gmailStatus.connected && (
                <Button
                  onClick={handleTestConnection}
                  disabled={testing}
                  variant="outline"
                  className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                  data-testid="test-gmail-btn"
                >
                  {testing ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Testing...
                    </span>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Test Connection
                    </>
                  )}
                </Button>
              )}
              <Button
                onClick={handleConnect}
                disabled={connecting}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="add-gmail-btn"
              >
                {connecting ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Connecting...
                  </span>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Account
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-24">
              <div className="w-6 h-6 border-2 border-slate-200 border-t-slate-800 rounded-full animate-spin" />
            </div>
          ) : gmailStatus.accounts && gmailStatus.accounts.length > 0 ? (
            <div className="space-y-3">
              {gmailStatus.accounts.map((account) => (
                <div 
                  key={account.id} 
                  className={`flex items-center gap-4 p-4 rounded-lg border ${
                    account.is_primary 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : 'bg-slate-50 border-slate-200'
                  }`}
                  data-testid={`email-account-${account.email}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    account.is_primary ? 'bg-emerald-100' : 'bg-slate-100'
                  }`}>
                    {account.is_primary ? (
                      <Star className="w-5 h-5 text-emerald-600 fill-emerald-600" />
                    ) : (
                      <User className="w-5 h-5 text-slate-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`font-medium ${account.is_primary ? 'text-emerald-900' : 'text-slate-900'}`}>
                        {account.display_name || account.name || account.email}
                      </p>
                      {account.is_primary && (
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-medium rounded">
                          Primary
                        </span>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDisplayName(account)}
                        className="text-slate-400 hover:text-[#165581] h-6 w-6 p-0"
                        title="Edit sender name"
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                    </div>
                    <p className={`text-sm ${account.is_primary ? 'text-emerald-600' : 'text-slate-500'}`}>
                      {account.email}
                    </p>
                    {account.display_name && (
                      <p className="text-xs text-slate-400 mt-1">
                        Emails will be sent as: "{account.display_name}" &lt;{account.email}&gt;
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {!account.is_primary && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSetPrimary(account.id)}
                        className="text-slate-600 hover:text-emerald-600 hover:border-emerald-300"
                        data-testid={`set-primary-${account.email}`}
                      >
                        <Star className="w-4 h-4 mr-1" />
                        Set Primary
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDisconnectDialog(account)}
                      className="text-slate-400 hover:text-red-600"
                      data-testid={`disconnect-${account.email}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
              <p className="text-sm text-slate-500 mt-4">
                The primary account is used by default when sending campaigns. You can select a different account when creating a campaign.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-slate-400" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">No Accounts Connected</p>
                  <p className="text-sm text-slate-500">Connect a Gmail account to start sending campaigns</p>
                </div>
                <Button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="connect-gmail-btn"
                >
                  {connecting ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Connecting...
                    </span>
                  ) : (
                    <>
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Connect Gmail
                    </>
                  )}
                </Button>
              </div>
              <div className="text-sm text-slate-500 space-y-2">
                <p>By connecting your Gmail account, you authorize this app to:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Send emails on your behalf</li>
                  <li>Read your email address for identification</li>
                </ul>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* App Info */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading">About</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">App Name</span>
              <span className="font-medium text-slate-900">Email Campaign Pro</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Version</span>
              <span className="font-medium text-slate-900">2.0.0</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Max Templates</span>
              <span className="font-medium text-slate-900">20</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Email Accounts</span>
              <span className="font-medium text-slate-900">{gmailStatus.accounts?.length || 0} connected</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Disconnect Confirmation Dialog */}
      <AlertDialog open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect Account</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disconnect <strong>{accountToDisconnect?.email}</strong>? 
              {accountToDisconnect?.is_primary && gmailStatus.accounts?.length > 1 && (
                <span className="block mt-2 text-amber-600">
                  This is your primary account. Another account will be set as primary automatically.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisconnect}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-disconnect-btn"
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Display Name Dialog */}
      <Dialog open={editDisplayNameOpen} onOpenChange={setEditDisplayNameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Sender Name</DialogTitle>
            <DialogDescription>
              Set the display name that recipients will see when they receive emails from {editingAccount?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="displayName">Sender Display Name</Label>
              <Input
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="e.g., Book A Trade"
              />
              <p className="text-sm text-slate-500">
                Emails will appear as: "{displayName || 'Your Name'}" &lt;{editingAccount?.email}&gt;
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDisplayNameOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateDisplayName}
              className="bg-[#165581] hover:bg-[#1a6a9e]"
              disabled={!displayName.trim()}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
